/**
 * Demo download guard.
 *
 * Every export/download in the AML panel is triggered via direct
 * browser navigation — `window.open(...)` for full-page exports, or
 * `<a href="/aml/.../download" target="_blank">` for inline file
 * links. Both bypass axios entirely, so the demo's axios adapter
 * never sees them. Without this guard those clicks hit the real
 * Laravel backend over the demo visitor's normal browser session
 * — which means a visitor who happens to be logged into the real
 * platform in another tab gets THEIR firm's actual data.
 *
 * This guard:
 *   1) Monkey-patches window.open so /aml/.../{export,download} URLs
 *      are caught before any real navigation happens.
 *   2) Adds a capture-phase document click listener so anchor-tag
 *      downloads with target=_blank are intercepted in the same way.
 *
 * For any matched URL it builds a Blob with a visibly-demo placeholder
 * payload and triggers a real download via createObjectURL. The
 * filename derives from the URL path so visitors get something
 * recognisable like `demo-program-risk-assessment.txt`. Plain text
 * (rather than fake PDFs) keeps the placeholder honest about what
 * it is.
 *
 * Install path: bootstrap.js calls installDownloadGuard() before any
 * Vue component mounts, ahead of even the dynamic import('./install.js')
 * for handler modules. That ordering matters — a download triggered
 * during the panel's initial render must already see the override.
 *
 * Verification surface: window.__demoDownloadGuard exposes the tracked
 * download log + the predicate functions for live programmatic checks.
 */

let _origWindowOpen = null;

const DEMO_PATH_PREFIX = '/aml/';

const _tracked = [];

/**
 * Decide whether a given URL is a demo download we should intercept.
 *
 * The pattern is intentionally broad: anything under /aml/ whose path
 * contains /export or ends with /download. That catches:
 *   - /aml/program/export/program-document
 *   - /aml/program/export/risk-assessment
 *   - /aml/program/export/change-log
 *   - /aml/training/export/my-record
 *   - /aml/training/export/report
 *   - /aml/reports/export/summary
 *   - /aml/reports/{id}/export
 *   - /aml/clients/{id}/export
 *   - /aml/personnel/{id}/export
 *   - /aml/escalations/export/register
 *   - /aml/compliance-log/export
 *   - /aml/attachments/{id}/download
 *   - /aml/acr/{id}/download
 *
 * Plus one static-asset family: /assets/document/... — the "Free
 * Documents" catalog cards link straight to real .doc/.pdf files on
 * the production host, so without interception a demo visitor gets
 * the genuine subscriber documents.
 *
 * Anything else outside /aml/ (including external links) is left alone
 * so we never accidentally hijack /macquarie-aml or third-party links.
 */
export function isDemoDownload(url) {
    if (!url || typeof url !== 'string') return false;
    let path;
    try {
        path = new URL(url, window.location.origin).pathname;
    } catch (_) {
        return false;
    }
    // /assets/document/... — Free Documents catalog files.
    if (path.startsWith('/assets/document/')) return true;
    if (!path.startsWith(DEMO_PATH_PREFIX)) return false;
    // /aml/.../export or /aml/.../export/...   (most exports)
    if (/\/export(\/|$)/.test(path)) return true;
    // /aml/.../download   (most file downloads)
    if (path.endsWith('/download')) return true;
    // /aml/audit-export/csv or /word — Service Audit
    // (`/audit-export/` doesn't match the /export/ regex above because
    // there's no slash before "export".)
    if (/\/audit-export\/(csv|word)$/.test(path)) return true;
    // /aml/training/records/{id}/certificate — per-completion PDF cert
    if (/\/training\/records\/\d+\/certificate$/.test(path)) return true;
    // /aml/training/modules/{id}/document — module reference PDF
    if (/\/training\/modules\/\d+\/document$/.test(path)) return true;
    // /aml/macquarie-tv/modules/{id}/audio — podcast download
    if (/\/macquarie-tv\/modules\/\d+\/audio$/.test(path)) return true;
    // /aml/macquarie-tv/modules/{id}/docs/{docId} — companion doc
    if (/\/macquarie-tv\/modules\/\d+\/docs\/\d+$/.test(path)) return true;
    return false;
}

/**
 * Derive a human-recognisable filename from the URL. Used as a
 * fallback when the content generator either hasn't been loaded yet
 * or doesn't recognise the URL pattern. The generator itself returns
 * a more specific filename (with the right extension — .csv for
 * tabular exports, .txt for documents).
 */
export function suggestedFilename(url) {
    let path = url;
    try {
        path = new URL(url, window.location.origin).pathname;
    } catch (_) { /* fall through */ }
    const segs = path.split('/').filter(Boolean);
    // Drop the leading 'aml' segment — every demo download is under it.
    if (segs[0] === 'aml') segs.shift();
    const slug = segs.join('-') || 'export';
    return `demo-${slug}.txt`;
}

function buildFallbackPlaceholderBlob(url) {
    // Used only if the content generator import itself fails. Normal
    // path goes through demoExportContent.generateExport() which has
    // its own per-URL placeholder for unmatched patterns.
    let path = url;
    try {
        path = new URL(url, window.location.origin).pathname;
    } catch (_) { /* fall through */ }
    const body = [
        'MACQUARIE AML — Demo placeholder',
        '',
        `Endpoint:  ${path}`,
        `Generated: ${new Date().toISOString()}`,
        '',
        'Demo content failed to load. Sign up at',
        'https://www.macquariegs.com.au for the live export.',
    ].join('\n');
    return new Blob([body], { type: 'text/plain;charset=utf-8' });
}

/**
 * Materialise the export file in the browser's download flow.
 *
 * Uses an off-DOM <a download> element so the file actually lands in
 * the user's Downloads folder rather than just opening a Blob URL in
 * a new tab — same UX as a real export.
 *
 * Content is generated by the lazily-loaded demoExportContent module,
 * which knows how to turn each URL into appropriate fixture-backed
 * data (a single-client dossier, a CSV of escalations, the program
 * document, etc.). If that import fails we fall back to a tiny
 * placeholder so the click never hangs.
 */
async function triggerDemoDownload(url) {
    let result;
    try {
        const mod = await import('./demoExportContent.js');
        result = await mod.generateExport(url);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[demo] content generator failed for', url, err);
        result = {
            filename: suggestedFilename(url),
            content: null,
            mime: 'text/plain;charset=utf-8',
        };
    }

    let blob;
    if (result.content == null) {
        blob = buildFallbackPlaceholderBlob(url);
    } else if (result.content instanceof Uint8Array || result.content instanceof ArrayBuffer) {
        // Binary export (XLSX, PDF). Pass through Blob without text
        // encoding so byte sequences (ZIP headers, CRC32, PDF binary
        // markers) survive intact.
        blob = new Blob([result.content], { type: result.mime || 'application/octet-stream' });
    } else if (typeof result.content === 'string' && result.mime === 'application/pdf') {
        // Legacy path — PDF content arrived as a binary string. Convert
        // each char to a byte so the Blob isn't UTF-8-mangled. Newer
        // builders return Uint8Array directly and take the branch above.
        const bytes = new Uint8Array(result.content.length);
        for (let i = 0; i < result.content.length; i++) {
            bytes[i] = result.content.charCodeAt(i) & 0xff;
        }
        blob = new Blob([bytes], { type: result.mime });
    } else {
        blob = new Blob([result.content], { type: result.mime || 'text/plain;charset=utf-8' });
    }
    const filename = result.filename || suggestedFilename(url);
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.style.display = 'none';
    // Tag this anchor so the document click listener can recognise its
    // own re-entry and skip it. Plain `isTrusted === false` would skip
    // legitimate programmatic clicks from app code too — we only want
    // to skip the synthetic click below.
    a.setAttribute('data-demo-internal', '1');
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        try { document.body.removeChild(a); } catch (_) { /* removed already */ }
        URL.revokeObjectURL(objectUrl);
    }, 200);

    _tracked.push({ originalUrl: url, filename, ts: Date.now() });
    // Soft info-level log so the developer can see exactly which URL
    // was intercepted. Not noisy enough to bother visitors.
    // eslint-disable-next-line no-console
    console.info('[demo] intercepted download:', url, '→', filename);
}

/**
 * Install both intercept layers. Idempotent — calling twice is a no-op
 * after the first install.
 */
let _installed = false;
export function installDownloadGuard() {
    if (_installed) return;
    _installed = true;

    // 1) window.open override.
    _origWindowOpen = window.open.bind(window);
    window.open = function (url, ...rest) {
        if (isDemoDownload(url)) {
            triggerDemoDownload(url);
            // Return null to mimic a popup-blocked window.open — no
            // caller in the AML panel checks the return value, but
            // returning a stub avoids "Cannot read property" errors
            // if one ever does.
            return null;
        }
        return _origWindowOpen(url, ...rest);
    };

    // 2) Anchor-tag click intercept. Runs in the capture phase so we
    //    win against any Vue @click handler on the same element.
    document.addEventListener('click', (e) => {
        // Only left-click without modifier keys — keep middle-click /
        // ctrl-click / meta-click semantics (open in new tab) intact
        // for non-demo links.
        if (e.button && e.button !== 0) return;
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

        let el = e.target;
        while (el && el !== document.body && el !== document.documentElement) {
            if (el.tagName === 'A' && el.href) {
                // Skip the guard's own internal <a download> trigger so
                // we don't re-enter recursively when triggerDemoDownload
                // synthesises a click on its hidden anchor.
                if (el.hasAttribute('data-demo-internal')) return;
                if (isDemoDownload(el.href)) {
                    e.preventDefault();
                    e.stopPropagation();
                    triggerDemoDownload(el.href);
                    return;
                }
                // Any anchor we find first wins — don't keep walking
                // past it looking for another anchor.
                break;
            }
            el = el.parentElement;
        }
    }, true);

    // Expose tracking + predicates for tests / debugging.
    window.__demoDownloadGuard = {
        tracked: _tracked,
        isDemoDownload,
        suggestedFilename,
    };
}
